import type { ConversationConfig, ConversationType } from './types'

function assertConversationType<T extends ConversationType, N extends { type: ConversationType }>(
  conversation: N,
  type: T
): asserts conversation is N & { type: T } {
  if (conversation.type !== type) {
    throw new Error(`Expected '${type}', got ${conversation.type}`)
  }
}

const CONVERSATION_TYPE_TO_GET_KEY: Record<
  ConversationType,
  ({ conversation }: { conversation: ConversationConfig }) => string
> = {
  direct: ({ conversation }) => {
    assertConversationType(conversation, 'direct')
    const { userIds } = conversation
    return `direct:${userIds.slice().sort().join(',')}`
  },
  'one-on-one-request': ({ conversation }) => {
    assertConversationType(conversation, 'one-on-one-request')
    const { userIds } = conversation
    return `one-on-one-request:${userIds.slice().sort().join(',')}`
  },
  'series-group': ({ conversation }) => {
    assertConversationType(conversation, 'series-group')
    const { seriesId } = conversation
    return `series-group:${seriesId}`
  },
  'series-direct': ({ conversation }) => {
    assertConversationType(conversation, 'series-direct')
    const { seriesId, userIds } = conversation
    return `series-direct:${seriesId}:${userIds.slice().sort().join(',')}`
  },
  'series-tutors': ({ conversation }) => {
    assertConversationType(conversation, 'series-tutors')
    const { seriesId } = conversation
    return `series-tutors:${seriesId}`
  },
  'series-ai': ({ conversation }) => {
    assertConversationType(conversation, 'series-ai')
    const { seriesId, userIds } = conversation
    return `series-ai:${seriesId}:${userIds.slice().sort().join(',')}`
  },
  'session-request': ({ conversation }) => {
    assertConversationType(conversation, 'session-request')
    const { sessionRequestId } = conversation
    return `session-request:${sessionRequestId}`
  },
  'session-direct': ({ conversation }) => {
    assertConversationType(conversation, 'session-direct')
    const { sessionId, userIds } = conversation
    return `session-direct:${sessionId}:${userIds.slice().sort().join(',')}`
  },
  'session-group': ({ conversation }) => {
    assertConversationType(conversation, 'session-group')
    const { sessionId } = conversation
    return `session-group:${sessionId}`
  },
  'session-tutors': ({ conversation }) => {
    assertConversationType(conversation, 'session-tutors')
    const { sessionId } = conversation
    return `session-tutors:${sessionId}`
  },
}

export default function getConversationKey({
  conversation,
}: {
  conversation: ConversationConfig
}): string {
  return CONVERSATION_TYPE_TO_GET_KEY[conversation.type]({ conversation })
}
